"use client";

/**
 * Reusable three-dot (kebab) menu button
 * Used in SectionItem and RightSidebar for section options
 */

import type React from "react";
import { MoreVertical } from "lucide-react";

interface OptionsMenuButtonProps {
  /** Click handler for the button */
  onClick: (e: React.MouseEvent) => void;
  /** Accessible label describing what this menu controls */
  ariaLabel: string;
  /** Whether the menu is currently expanded (for aria-expanded) */
  ariaExpanded?: boolean;
  /** ID of the controlled menu element (for aria-controls) */
  ariaControls?: string;
  /** Additional CSS classes to merge with the base styles */
  className?: string;
}

/**
 * Three-dot options menu trigger button
 * Stops click propagation to prevent parent click handlers from firing
 */
export function OptionsMenuButton({
  onClick,
  ariaLabel,
  ariaExpanded,
  ariaControls,
  className,
}: OptionsMenuButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      className={`flex items-center rounded ring-offset-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${className || "text-gray-400 transition-colors hover:text-gray-600"}`}
      aria-label={ariaLabel}
      aria-controls={ariaControls}
      {...(ariaExpanded !== undefined && { "aria-haspopup": "menu" as const, "aria-expanded": ariaExpanded })}
    >
      <MoreVertical className="h-4 w-4" />
    </button>
  );
}
